import { User } from '@adtech-web/login-provider-client';
import TworkLoginProvider from '@adtech-web/twork-login-provider';
import type { ILoginProvider } from 'components-machine';
import { base64url, createRemoteJWKSet, decodeJwt, jwtVerify } from 'jose';
import { sendError } from './sentry';
import { inIframe } from './utils/location';

export class UnauthorizedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnauthorizedError';
  }
}

export class ForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ForbiddenError';
  }
}

// prettier-ignore
const AUTH_DISABLED_USER: User = {id: 'fa5a115c-2e45-4abf-9552-9099be922e36', first_name: 'Царь', last_name: 'Ненастоящий'};
const AUTH_DISABLED_KEY = '@rtb/login-provider-client/poll-components-fake';
const AUTH_DISABLED = inIframe({ ignoreForce: true });

const STORAGE_DISABLED = (function () {
  const testKey = '@rtb/polls/temp';
  try {
    localStorage.setItem(testKey, testKey);
    localStorage.removeItem(testKey);
    return false;
  } catch {
    return true;
  }
})();

const loginProviderMock: ILoginProvider = {
  fetchAuthorizationCode() {
    window.localStorage.setItem(AUTH_DISABLED_KEY, '1');
    window.location.reload();
    return Promise.resolve();
  },
  fetchUser<T>() {
    if (window.localStorage.getItem(AUTH_DISABLED_KEY) === '1') {
      return Promise.resolve(AUTH_DISABLED_USER as T);
    }
    return Promise.reject();
  },
  getAccessToken() {
    if (window.localStorage.getItem(AUTH_DISABLED_KEY) === '1') {
      return Promise.resolve({ token: { value: 'TOKEN_MOCK', expiry: new Date().toISOString() } });
    }
    return Promise.reject();
  },
};

const storageDisabledMock: ILoginProvider = {
  fetchAuthorizationCode() {
    return Promise.reject();
  },
  fetchUser() {
    return Promise.reject();
  },
  getAccessToken() {
    return Promise.reject();
  },
};

export function getLoginProvider(): ILoginProvider {
  if (STORAGE_DISABLED) return storageDisabledMock;
  if (AUTH_DISABLED || window.APP_TEST_AUTH_DISABLED) return loginProviderMock;

  const REDIRECT_URLS = window.APP_TWORK_REDIRECT_URL
    ? window.APP_TWORK_REDIRECT_URL.split(',').map(it => new URL(it))
    : [];
  const REDIRECT_URL = REDIRECT_URLS.find(it => it.host === window.location.host) || REDIRECT_URLS[0];

  if (!REDIRECT_URL) throw new Error('REDIRECT_URL is necessary!');

  const provider = new TworkLoginProvider({
    originUrl: window.APP_TWORK_LOGIN_URL,
    clientId: window.APP_TWORK_CLIENT_ID,
    scopes: ['openid'],
    redirectUrl: REDIRECT_URL.toString(),
    jwtProvider: {
      async jwtVerify(jwt: string | Uint8Array, jwksUrl: URL) {
        const jwks = createRemoteJWKSet(jwksUrl);
        const tenYearsInSeconds = 60 * 60 * 24 * 365 * 10;

        const plainJwt = () => decodeJwt(typeof jwt === 'string' ? jwt : base64url.encode(jwt));
        const debugJwt = () => {
          try {
            const { nbf, exp, iat } = plainJwt();
            return { nbf, exp, iat };
          } catch {
            return null;
          }
        };

        try {
          return await jwtVerify(jwt, jwks).then(({ payload }) => payload);
        } catch (error) {
          sendError(new Error('Strict JWT check failed! '), {
            actualError: error,
            additionalInfo: { ...debugJwt() },
            severity: 'MINOR',
          });
          try {
            return await jwtVerify(jwt, jwks, {
              clockTolerance: tenYearsInSeconds,
              maxTokenAge: tenYearsInSeconds,
            }).then(({ payload }) => payload);
          } catch (error) {
            sendError(new Error('Loose JWT check failed!'), {
              actualError: error,
              additionalInfo: { ...debugJwt() },
              severity: 'MINOR',
            });
            return plainJwt();
          }
        }
      },
    },
  });

  return {
    fetchAuthorizationCode: () => {
      const BACK_URL_KEY = '@tinkoff/polls/backUrl';

      try {
        const backUrl = window.location.href.slice(window.location.origin.length);
        localStorage.setItem(BACK_URL_KEY, encodeURIComponent(backUrl));
      } catch {
        /* empty */
      }
      provider.fetchAuthorizationCode();
    },
    fetchUser: async config => {
      try {
        return await provider.fetchUser(config);
      } catch (error) {
        if (error instanceof Error && ['No authorization code', 'Access token not found'].includes(error.message))
          return undefined;
        throw error;
      }
    },
    getAccessToken: async () => {
      try {
        return await provider.getAccessToken();
      } catch (error) {
        if (error instanceof Error && ['No authorization code', 'Access token not found'].includes(error.message))
          return undefined;
        throw error;
      }
    },
  };
}
